import React, {useEffect, useRef, useState} from 'react';
import AddIcon from '@mui/icons-material/Add';
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Fab,
    FormControl,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import {
    createCodelist,
    listCodelistEntries,
    listCodelists,
    refreshCodelist,
    updateCodelist
} from '../api/dataApi';
import {CodelistCatalog, CodelistCatalogCreate, CodelistLookupEntry, Page} from '../types';

type Snack = { open: boolean; message: string; severity: 'success' | 'error' };
type EnabledFilter = 'all' | 'enabled' | 'disabled';

const emptyPage = <T,>(): Page<T> => ({content: [], number: 0, size: 20, totalElements: 0, totalPages: 0});

function formatDate(value?: string | null) {
    if (!value) return '-';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat(undefined, {
        year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'
    }).format(date);
}

function refreshErrorMessage(error: any) {
    return error?.response?.status === 409
        ? 'Refresh is already in progress for this codelist'
        : 'Failed to refresh codelist';
}

export default function CodelistsPage() {
    const [catalogues, setCatalogues] = useState<Page<CodelistCatalog>>(emptyPage<CodelistCatalog>());
    const [search, setSearch] = useState('');
    const [enabledFilter, setEnabledFilter] = useState<EnabledFilter>('all');
    const [loading, setLoading] = useState(false);
    const [updating, setUpdating] = useState<Record<string, boolean>>({});
    const [snack, setSnack] = useState<Snack>({open: false, message: '', severity: 'success'});
    const [editing, setEditing] = useState<CodelistCatalog | null>(null);
    const [configOpen, setConfigOpen] = useState(false);
    const [refreshing, setRefreshing] = useState<CodelistCatalog | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [entriesFor, setEntriesFor] = useState<CodelistCatalog | null>(null);
    const [entries, setEntries] = useState<Page<CodelistLookupEntry>>(emptyPage<CodelistLookupEntry>());
    const [entriesLoading, setEntriesLoading] = useState(false);
    const [entrySearch, setEntrySearch] = useState('');
    const catalogueRequestSequence = useRef(0);
    const entryRequestSequence = useRef(0);

    async function loadCatalogues(page = catalogues.number, size = catalogues.size, filter = search, enabled = enabledFilter) {
        const requestSequence = ++catalogueRequestSequence.current;
        setLoading(true);
        try {
            const result = await listCodelists(page, size, filter, enabled === 'all' ? undefined : enabled === 'enabled');
            if (requestSequence === catalogueRequestSequence.current) {
                setCatalogues(result);
            }
        } catch {
            if (requestSequence === catalogueRequestSequence.current) {
                setSnack({open: true, message: 'Failed to load codelists', severity: 'error'});
            }
        } finally {
            if (requestSequence === catalogueRequestSequence.current) {
                setLoading(false);
            }
        }
    }

    async function loadEntries(page = entries.number, size = entries.size, filter = entrySearch) {
        if (!entriesFor) return;
        const requestSequence = ++entryRequestSequence.current;
        setEntriesLoading(true);
        try {
            const result = await listCodelistEntries(entriesFor.id, page, size, filter);
            if (requestSequence === entryRequestSequence.current) {
                setEntries(result);
            }
        } catch {
            if (requestSequence === entryRequestSequence.current) {
                setSnack({open: true, message: 'Failed to load active entries', severity: 'error'});
            }
        } finally {
            if (requestSequence === entryRequestSequence.current) {
                setEntriesLoading(false);
            }
        }
    }

    useEffect(() => {
        catalogueRequestSequence.current += 1;
        const timeout = window.setTimeout(() => loadCatalogues(0, catalogues.size), 300);
        return () => window.clearTimeout(timeout);
        // The request changes only with the filters; pagination uses explicit handlers below.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, enabledFilter]);

    useEffect(() => {
        entryRequestSequence.current += 1;
        if (!entriesFor) return;
        const timeout = window.setTimeout(() => loadEntries(0, entries.size), entrySearch ? 300 : 0);
        return () => window.clearTimeout(timeout);
        // The request changes only with the active-entry dialog/filter.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entriesFor, entrySearch]);

    useEffect(() => () => {
        catalogueRequestSequence.current += 1;
        entryRequestSequence.current += 1;
    }, []);

    function changeCatalogueSearch(value: string) {
        catalogueRequestSequence.current += 1;
        setSearch(value);
    }

    function changeEnabledFilter(value: EnabledFilter) {
        catalogueRequestSequence.current += 1;
        setEnabledFilter(value);
    }

    function openEntries(catalogue: CodelistCatalog) {
        entryRequestSequence.current += 1;
        setEntries(emptyPage<CodelistLookupEntry>());
        setEntrySearch('');
        setEntriesFor(catalogue);
    }

    function changeEntrySearch(value: string) {
        entryRequestSequence.current += 1;
        setEntrySearch(value);
    }

    function closeEntries() {
        entryRequestSequence.current += 1;
        setEntriesFor(null);
        setEntrySearch('');
        setEntries(emptyPage<CodelistLookupEntry>());
    }

    async function saveCatalogue(form: CodelistCatalogCreate) {
        if (!form.codelistCode.trim() || !form.name.trim()) {
            setSnack({open: true, message: 'Code and name are required', severity: 'error'});
            return;
        }
        setSubmitting(true);
        try {
            const payload = {...form, codelistCode: form.codelistCode.trim(), name: form.name.trim()};
            if (editing) {
                await updateCodelist(editing.id, payload);
                setSnack({open: true, message: 'Codelist updated', severity: 'success'});
            } else {
                await createCodelist(payload);
                setSnack({open: true, message: 'Codelist created', severity: 'success'});
            }
            setConfigOpen(false);
            setEditing(null);
            await loadCatalogues(editing ? catalogues.number : 0);
        } catch {
            setSnack({open: true, message: editing ? 'Failed to update codelist' : 'Failed to create codelist', severity: 'error'});
        } finally {
            setSubmitting(false);
        }
    }

    async function toggleEnabled(item: CodelistCatalog, enabled: boolean) {
        setUpdating(current => ({...current, [item.id]: true}));
        try {
            await updateCodelist(item.id, {enabled});
            setSnack({open: true, message: `Codelist ${enabled ? 'enabled' : 'disabled'}`, severity: 'success'});
            await loadCatalogues();
        } catch {
            setSnack({open: true, message: 'Failed to update codelist', severity: 'error'});
        } finally {
            setUpdating(current => {
                const {[item.id]: ignored, ...rest} = current;
                return rest;
            });
        }
    }

    async function confirmRefresh() {
        if (!refreshing) return;
        setSubmitting(true);
        try {
            const result = await refreshCodelist(refreshing.id);
            if (result.successful) {
                setSnack({open: true, message: 'Codelist refreshed', severity: 'success'});
            } else {
                setSnack({open: true, message: result.errorMessage || 'Codelist refresh failed', severity: 'error'});
            }
            setRefreshing(null);
            await loadCatalogues();
        } catch (error) {
            setSnack({open: true, message: refreshErrorMessage(error), severity: 'error'});
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, width: '100%', maxWidth: 'calc(100dvw - 100px)', boxSizing: 'border-box', mx: 'auto', px: 2}}>
            {loading && <LinearProgress/>}
            <Stack direction={{xs: 'column', sm: 'row'}} spacing={2} alignItems={{sm: 'center'}}>
                <Typography variant="h5" sx={{mr: 'auto'}}>Codelists</Typography>
                <TextField label="Code or name" value={search} onChange={event => changeCatalogueSearch(event.target.value)} placeholder="Search..." sx={{minWidth: 240}}/>
                <FormControl sx={{minWidth: 150}}>
                    <InputLabel id="codelist-enabled-filter">Enabled</InputLabel>
                    <Select labelId="codelist-enabled-filter" label="Enabled" value={enabledFilter} onChange={event => changeEnabledFilter(event.target.value as EnabledFilter)}>
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="enabled">Enabled</MenuItem>
                        <MenuItem value="disabled">Disabled</MenuItem>
                    </Select>
                </FormControl>
            </Stack>

            <TableContainer component={Paper} sx={{overflowX: 'auto'}}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Code</TableCell><TableCell>Name</TableCell><TableCell>Language</TableCell><TableCell align="center">Enabled</TableCell>
                            <TableCell>Interval</TableCell><TableCell>Last refresh</TableCell><TableCell>Next refresh</TableCell><TableCell>Outcome</TableCell><TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {!loading && catalogues.content.length === 0 && <TableRow><TableCell colSpan={9}>No codelists found</TableCell></TableRow>}
                        {catalogues.content.map(item => (
                            <TableRow key={item.id} hover onDoubleClick={() => { setEditing(item); setConfigOpen(true); }}>
                                <TableCell>{item.codelistCode}</TableCell><TableCell>{item.name}</TableCell><TableCell>{item.language}</TableCell>
                                <TableCell align="center"><Switch checked={item.enabled} disabled={loading || !!updating[item.id]} onChange={event => toggleEnabled(item, event.target.checked)} inputProps={{'aria-label': `Enable ${item.codelistCode}`}}/></TableCell>
                                <TableCell>{item.refreshIntervalMinutes} min</TableCell><TableCell>{formatDate(item.lastRefreshAt)}</TableCell><TableCell>{formatDate(item.nextRefreshAt)}</TableCell>
                                <TableCell sx={{maxWidth: 200}}>{item.lastRefreshStatus ? <Tooltip title={item.lastRefreshError || ''}><Chip size="small" color={item.lastRefreshStatus === 'SUCCESS' ? 'success' : 'error'} label={item.lastRefreshStatus}/></Tooltip> : '-'}</TableCell>
                                <TableCell align="right" sx={{whiteSpace: 'nowrap'}}>
                                    <Button size="small" onClick={() => { setEditing(item); setConfigOpen(true); }}>Edit</Button>
                                    <Button size="small" onClick={() => openEntries(item)}>Entries</Button>
                                    <Button size="small" color="primary" onClick={() => setRefreshing(item)}>Refresh</Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination component="div" count={catalogues.totalElements} page={catalogues.number} rowsPerPage={catalogues.size}
                             onPageChange={(_, page) => loadCatalogues(page)} onRowsPerPageChange={event => loadCatalogues(0, Number(event.target.value))}
                             rowsPerPageOptions={[5, 10, 20, 50]}/>

            <Tooltip title="Add codelist"><Fab color="primary" aria-label="add codelist" onClick={() => { setEditing(null); setConfigOpen(true); }} sx={{position: 'fixed', bottom: 24, right: 24}}><AddIcon/></Fab></Tooltip>
            <CodelistConfigDialog open={configOpen} item={editing} saving={submitting} onClose={() => { setConfigOpen(false); setEditing(null); }} onSave={saveCatalogue}/>
            <Dialog open={!!refreshing} onClose={() => !submitting && setRefreshing(null)}>
                <DialogTitle>Refresh codelist</DialogTitle><DialogContent>Fetch the latest English labels for <strong>{refreshing?.codelistCode}</strong> now?</DialogContent>
                <DialogActions><Button onClick={() => setRefreshing(null)} disabled={submitting}>Cancel</Button><Button variant="contained" onClick={confirmRefresh} disabled={submitting}>Refresh</Button></DialogActions>
            </Dialog>
            <EntriesDialog catalogue={entriesFor} entries={entries} loading={entriesLoading} search={entrySearch} onSearch={changeEntrySearch} onClose={closeEntries} onChangePage={page => loadEntries(page)} onChangeSize={size => loadEntries(0, size)}/>
            <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(current => ({...current, open: false}))} anchorOrigin={{vertical: 'top', horizontal: 'right'}}>
                <Alert severity={snack.severity} variant="filled" onClose={() => setSnack(current => ({...current, open: false}))}>{snack.message}</Alert>
            </Snackbar>
        </Box>
    );
}

function CodelistConfigDialog({open, item, saving, onClose, onSave}: { open: boolean; item: CodelistCatalog | null; saving: boolean; onClose: () => void; onSave: (form: CodelistCatalogCreate) => void }) {
    const [form, setForm] = useState<CodelistCatalogCreate>({codelistCode: '', name: '', enabled: true, refreshIntervalMinutes: 1440});

    useEffect(() => setForm(item ? {
        codelistCode: item.codelistCode, name: item.name, enabled: item.enabled, refreshIntervalMinutes: item.refreshIntervalMinutes
    } : {codelistCode: '', name: '', enabled: true, refreshIntervalMinutes: 1440}), [item, open]);

    return <Dialog open={open} onClose={() => !saving && onClose()} fullWidth maxWidth="sm">
        <DialogTitle>{item ? 'Edit codelist' : 'Add codelist'}</DialogTitle>
        <Box component="form" onSubmit={event => { event.preventDefault(); onSave(form); }}>
            <DialogContent dividers><Stack spacing={2}>
                <TextField autoFocus required label="Codelist code" value={form.codelistCode} onChange={event => setForm(current => ({...current, codelistCode: event.target.value}))}/>
                <TextField required label="Name" value={form.name} onChange={event => setForm(current => ({...current, name: event.target.value}))}/>
                <TextField required label="Refresh interval (minutes)" type="number" inputProps={{min: 1, max: 525600}} value={form.refreshIntervalMinutes} onChange={event => setForm(current => ({...current, refreshIntervalMinutes: Number(event.target.value)}))}/>
                <Stack direction="row" alignItems="center" spacing={1}><Switch checked={!!form.enabled} onChange={event => setForm(current => ({...current, enabled: event.target.checked}))}/><Typography>Enabled</Typography></Stack>
            </Stack></DialogContent>
            <DialogActions><Button onClick={onClose} disabled={saving}>Cancel</Button><Button type="submit" variant="contained" disabled={saving}>Save</Button></DialogActions>
        </Box>
    </Dialog>;
}

function EntriesDialog({catalogue, entries, loading, search, onSearch, onClose, onChangePage, onChangeSize}: { catalogue: CodelistCatalog | null; entries: Page<CodelistLookupEntry>; loading: boolean; search: string; onSearch: (search: string) => void; onClose: () => void; onChangePage: (page: number) => void; onChangeSize: (size: number) => void }) {
    return <Dialog open={!!catalogue} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>Active entries{catalogue ? `: ${catalogue.codelistCode}` : ''}</DialogTitle>
        <DialogContent dividers><Stack spacing={2}>
            <TextField autoFocus label="Code or label" value={search} onChange={event => onSearch(event.target.value)} placeholder="Search active entries..."/>
            {loading && <LinearProgress/>}
            <Table size="small"><TableHead><TableRow><TableCell>Code</TableCell><TableCell>Label</TableCell><TableCell>Language</TableCell><TableCell>Refreshed</TableCell></TableRow></TableHead>
                <TableBody>{!loading && entries.content.length === 0 ? <TableRow><TableCell colSpan={4}>No active entries found</TableCell></TableRow> : entries.content.map(entry => <TableRow key={entry.code}><TableCell>{entry.code}</TableCell><TableCell>{entry.label}</TableCell><TableCell>{entry.language}</TableCell><TableCell>{formatDate(entry.refreshedAt)}</TableCell></TableRow>)}</TableBody>
            </Table>
            <TablePagination component="div" count={entries.totalElements} page={entries.number} rowsPerPage={entries.size} onPageChange={(_, page) => onChangePage(page)} onRowsPerPageChange={event => onChangeSize(Number(event.target.value))} rowsPerPageOptions={[5, 10, 20, 50]}/>
        </Stack></DialogContent>
        <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>;
}